import type { DropdownGroup } from '@sparklayer-io/spark-dash-ui/components/ui/dropdown/types';
import { useStore } from '@tanstack/react-form';
import { Card, Checkbox, Dropdown, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { useSettings } from '../../../../context/settings-context';
import { toggleUndefined } from '../../../../utils/form';
import { usePriceList } from '../../hooks/use-price-list';
import { usePriceListForm } from '../../hooks/use-price-list-form';
import { useAllPriceListsWithManualPriceCounts } from '../../hooks/use-price-lists';
import { PRICE_LIST_SOURCES, type PriceListApiResponse } from '../../types';
import { PercentageVariationForm } from './percentage-variation-form';

function ExchangeRate() {
  const form = usePriceListForm();
  const settings = useSettings();

  const currency = useStore(form.store, (state) => state.values.currency);

  return (
    <span className="inline-flex">
      <form.Field name="automaticPricing.exchangeRate">
        {(field) => (
          <Input
            suffix={currency}
            value={field.state.value}
            prefix={`1 ${settings.baseCurrency} =`}
            error={field.state.meta.errors[0]?.message}
            onChange={field.handleChange}
            inputMode="decimal"
          />
        )}
      </form.Field>
    </span>
  );
}

function groupPriceLists(priceLists: PriceListApiResponse[], currentSlug: string): DropdownGroup[] {
  const optionGroups: Record<string, DropdownGroup> = {
    [PRICE_LIST_SOURCES.CUSTOM]: {
      title: t('price_list_builder.automatic_pricing.pricing_source.groups.custom'),
      options: [],
    },
    [PRICE_LIST_SOURCES.PLATFORM]: {
      title: t('price_list_builder.automatic_pricing.pricing_source.groups.platform'),
      options: [],
    },
    [PRICE_LIST_SOURCES.INTEGRATION]: {
      title: t('price_list_builder.automatic_pricing.pricing_source.groups.integration'),
      options: [],
    },
  };

  for (const list of priceLists) {
    if (list.slug === currentSlug) {
      continue;
    }

    // Price List which has rules cannot be used i.e. automated price lists cannot be stacked
    if (list.rules.length) {
      continue;
    }

    optionGroups[list.source]?.options.push({
      label: list.name,
      value: list.slug,
    });
  }

  return Object.values(optionGroups).filter((group) => group.options.length > 0);
}

function AutomaticPricing(props: { priceListOptions: DropdownGroup[] }) {
  const form = usePriceListForm();
  const settings = useSettings();

  const currency = useStore(form.store, (state) => state.values.currency);
  const automaticPricing = useStore(form.store, (state) => state.values.automaticPricing);
  const currenciesMatch = currency === settings.baseCurrency;

  useEffect(() => {
    if (currenciesMatch) {
      form.setFieldValue('automaticPricing.exchangeRate', undefined);
    }
  }, [currenciesMatch, form]);

  const { data: sourceList } = usePriceList(automaticPricing?.priceListSource ?? '', {
    enabled: !!automaticPricing?.priceListSource,
  });

  if (!automaticPricing) {
    return null;
  }

  const description = t(
    currenciesMatch
      ? 'price_list_builder.automatic_pricing.currency_conversion.same_currency'
      : 'price_list_builder.automatic_pricing.currency_conversion.enable.description',
    { currency: currency },
  );

  return (
    <div className="flex flex-col gap-3">
      <form.Field name="automaticPricing.priceListSource">
        {(field) => (
          <Dropdown
            label={t('price_list_builder.automatic_pricing.pricing_source.label')}
            value={field.state.value ?? ''}
            onChange={field.handleChange}
            error={field.state.meta.errors[0]?.message}
            options={props.priceListOptions}
            placeholder={t('price_list_builder.automatic_pricing.pricing_source.placeholder')}
          />
        )}
      </form.Field>
      {sourceList && (
        <>
          <form.Field name="automaticPricing.percentageVariation">
            {(field) => (
              <Checkbox
                checked={field.state.value !== undefined}
                onChange={() =>
                  field.handleChange((prev) =>
                    toggleUndefined(prev, {
                      amount: '0',
                      direction: 'minus',
                    }),
                  )
                }
                error={field.state.meta.errors[0]?.message}
                label={t('price_list_builder.automatic_pricing.percentage_variation.enable')}
                description={t(
                  'price_list_builder.automatic_pricing.percentage_variation.description',
                  { name: sourceList.name },
                )}
              >
                <PercentageVariationForm />
              </Checkbox>
            )}
          </form.Field>
          {!currenciesMatch && (
            <form.Field name="automaticPricing.exchangeRate">
              {(field) => (
                <Checkbox
                  checked={field.state.value !== undefined}
                  disabled={!settings.baseCurrency || currenciesMatch}
                  onChange={() => field.handleChange((prev) => toggleUndefined(prev, '0'))}
                  error={field.state.meta.errors[0]?.message}
                  label={t('price_list_builder.automatic_pricing.currency_conversion.enable.label')}
                  description={description}
                >
                  <ExchangeRate />
                </Checkbox>
              )}
            </form.Field>
          )}
        </>
      )}
    </div>
  );
}

export function AutomaticPricingForm() {
  const { data: priceLists } = useAllPriceListsWithManualPriceCounts();

  const form = usePriceListForm();
  const handle = useStore(form.store, (state) => state.values.handle);

  const priceListOptions = useMemo(() => groupPriceLists(priceLists, handle), [priceLists, handle]);

  return (
    <Card>
      <Card.Header headingLevel="h2">{t('price_list_builder.automatic_pricing.title')}</Card.Header>
      <Card.Body>
        <Text size="md" variant="secondary">
          {t('price_list_builder.automatic_pricing.enable.description')}
        </Text>
        <form.Field name="automaticPricing">
          {(field) => (
            <Checkbox
              checked={!!field.state.value}
              // Disabled if there aren't any price lists
              disabled={!priceListOptions[0]?.options?.[0]}
              error={field.state.meta.errors[0]?.message}
              onChange={() =>
                field.handleChange((prev) =>
                  toggleUndefined(prev, {
                    priceListSource: '',
                  }),
                )
              }
              label={t('price_list_builder.automatic_pricing.enable.label')}
            >
              <AutomaticPricing priceListOptions={priceListOptions} />
            </Checkbox>
          )}
        </form.Field>
      </Card.Body>
    </Card>
  );
}
